import { trace, SpanStatusCode, type Span } from '@opentelemetry/api';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { SimpleSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import type { EventName } from '@opentelemetry/instrumentation-user-interaction';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { initClientErrorReporter } from './client-error-reporter';
import { hasCookieConsent } from './components/common/CookieConsentBanner';

declare global {
  interface Window {
    __jubielTelemetryInitialized?: boolean;
  }
}

// Use environment variable or construct from current origin
// Environment variable is set during build for each environment (dev/production)
// Fallback to current origin at runtime if not set
const OTEL_DEFAULT_ENDPOINT = typeof window !== 'undefined' 
  ? `${window.location.origin}/otel/v1/traces`
  : 'https://dev.wedistar.com/otel/v1/traces';
const tracer = trace.getTracer('jubiel-frontend-ui');

type MaybeRequest = Request | RequestInit | URL | string | undefined;

const sanitizePath = (path: string): string => {
  if (!path) {
    return '/';
  }

  let sanitized =
    path
      .replace(/^\/(ro|en)(?=\/)/i, '/:locale')
      .replace(/\/[0-9a-f]{8}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f]{12}/gi, '/:uuid')
      .replace(/\/[0-9a-f]{10,}/gi, '/:token')
      .replace(/\/\d+/g, '/:id')
      .replace(/\/{2,}/g, '/') || '/';

  if (sanitized.length > 1 && sanitized.endsWith('/')) {
    sanitized = sanitized.slice(0, -1);
  }

  return sanitized || '/';
};

const formatHttpSpanName = (method: string, url: string): { name: string; template?: string } => {
  try {
    const parsed = new URL(url, window.location.origin);
    const sanitizedPath = sanitizePath(parsed.pathname);
    return {
      name: `${method.toUpperCase()} ${sanitizedPath}`,
      template: sanitizedPath,
    };
  } catch {
    return { name: method.toUpperCase() };
  }
};

const getSpanAttribute = (span: Span, key: string): string | undefined => {
  const attributes = (span as unknown as { attributes?: Record<string, unknown> }).attributes;
  const value = attributes?.[key];
  return typeof value === 'string' ? value : undefined;
};

const extractFromRequest = (request: MaybeRequest): { url?: string; method?: string } => {
  if (!request) {
    return {};
  }

  if (typeof request === 'string') {
    return { url: request };
  }

  if (request instanceof URL) {
    return { url: request.href };
  }

  if (request instanceof Request) {
    return { url: request.url, method: request.method };
  }

  const requestInit = request as RequestInit & { url?: string };
  return { url: requestInit.url, method: requestInit.method };
};

const renameHttpSpan = (span: Span, defaults: { method?: string; url?: string } = {}) => {
  const method = (defaults.method || getSpanAttribute(span, 'http.method') || 'GET').toUpperCase();
  const url = defaults.url || getSpanAttribute(span, 'http.url') || getSpanAttribute(span, 'http.target');

  if (!url) {
    return;
  }

  const { name, template } = formatHttpSpanName(method, url);
  span.updateName(name);

  if (template) {
    span.setAttribute('jubiel.http.route_template', template);
  }
};

export function initWebTelemetry() {
  if (typeof window === 'undefined') {
    return;
  }

  // Kill switch: when VITE_OTEL_ENABLED is not 'true', do not collect any traces
  if (import.meta.env.VITE_OTEL_ENABLED !== 'true') {
    return;
  }

  // Disable OTEL in development environment unless explicitly enabled for debugging
  const isDev = import.meta.env.MODE === 'development' || import.meta.env.DEV;
  const otelEnableInDev = import.meta.env.VITE_ENABLE_OTEL === 'true';
  if (isDev && !otelEnableInDev) {
    // eslint-disable-next-line no-console
    console.info('[Telemetry] OpenTelemetry disabled in development (set VITE_ENABLE_OTEL=true to enable)');
    return;
  }

  const exporterUrl =
    import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT || OTEL_DEFAULT_ENDPOINT;
  // API URL is set during build for each environment
  // Fallback to current origin at runtime if not set
  const apiBaseUrl =
    import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? `${window.location.origin}/api` : 'https://dev.wedistar.com/api');

  const escapeRegex = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  let apiOriginPattern: RegExp | null = null;
  try {
    const apiOrigin = new URL(apiBaseUrl).origin;
    apiOriginPattern = new RegExp(`^${escapeRegex(apiOrigin)}`);
  } catch {
    // Fallback pattern - will match current origin
    apiOriginPattern = typeof window !== 'undefined' 
      ? new RegExp(`^${escapeRegex(window.location.origin)}`)
      : /https:\/\/dev\.wedistar\.com/;
  }

  const propagationTargets = apiOriginPattern ? [apiOriginPattern] : [];
  const ignoreExporterUrls = [
    /\/otel\/v1\/traces/,
    /@vite\/client/,
    /\/node_modules\/\.vite\//,
    /fonts\.googleapis\.com/,
    /fonts\.gstatic\.com/,
  ];

  const exporter = new OTLPTraceExporter({
    url: exporterUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [SemanticResourceAttributes.SERVICE_NAME]: 'jubiel-frontend',
      [SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT]:
        import.meta.env.MODE ?? 'development',
    }),
    spanProcessors: [new SimpleSpanProcessor(exporter)],
  });

  provider.register({
    contextManager: new ZoneContextManager(),
  });

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: propagationTargets,
        ignoreUrls: ignoreExporterUrls,
        applyCustomAttributesOnSpan: (span: Span, request: MaybeRequest) => {
          const { url, method } = extractFromRequest(request);
          renameHttpSpan(span, { url, method });
        },
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: propagationTargets,
        ignoreUrls: ignoreExporterUrls,
        applyCustomAttributesOnSpan: (span: Span, xhr: XMLHttpRequest) => {
          const enhanced = xhr as XMLHttpRequest & { __ot_method?: string };
          renameHttpSpan(span, {
            method: enhanced.__ot_method,
            url: xhr.responseURL,
          });
        },
      }),
      new UserInteractionInstrumentation({
        eventNames: ['click', 'submit', 'change', 'input'],
        shouldPreventSpanCreation: (_eventType: EventName, element: HTMLElement) =>
          element?.hasAttribute?.('data-telemetry-ignore') ?? false,
      }),
    ],
  });
}

// Initialize telemetry: kill switch first; then dev bypass (no consent) or require analytics consent
function initTelemetryIfConsented() {
  if (import.meta.env.VITE_OTEL_ENABLED !== 'true') {
    return;
  }
  const isDev = import.meta.env.MODE === 'development' || import.meta.env.DEV;
  const otelEnableInDev = import.meta.env.VITE_ENABLE_OTEL === 'true';
  if (isDev && otelEnableInDev) {
    initWebTelemetry();
    return;
  }
  if (hasCookieConsent('analytics')) {
    initWebTelemetry();
  }
}

// Initialize error reporter only if analytics consent is given
function initErrorReporterIfConsented() {
  if (hasCookieConsent('analytics')) {
    initClientErrorReporter();
  }
}

// Try to initialize on load
initTelemetryIfConsented();
initErrorReporterIfConsented();

// Listen for consent updates
if (typeof window !== 'undefined') {
  window.addEventListener('cookieConsentUpdated', () => {
    // Re-check consent and initialize if now consented
    if (hasCookieConsent('analytics')) {
      if (!window.__jubielTelemetryInitialized) {
        initWebTelemetry();
        window.__jubielTelemetryInitialized = true;
      }
      if (!window.__jubielErrorReporter) {
        initClientErrorReporter();
      }
    }
  });
}

type ReactCommitMeasurement = {
  id: string;
  phase: 'mount' | 'update' | 'nested-update';
  actualDuration: number;
  baseDuration: number;
  startTime: number;
  commitTime: number;
};

export function recordReactCommit(measurement: ReactCommitMeasurement): void {
  if (import.meta.env.VITE_OTEL_ENABLED !== 'true') {
    return;
  }
  const isDev = import.meta.env.MODE === 'development' || import.meta.env.DEV;
  const otelEnableInDev = import.meta.env.VITE_ENABLE_OTEL === 'true';
  if (isDev && !otelEnableInDev) {
    return;
  }

  tracer.startActiveSpan(`react.commit ${measurement.id}`, (span: Span) => {
    span.setAttributes({
      'react.phase': measurement.phase,
      'react.actual_duration_ms': measurement.actualDuration,
      'react.base_duration_ms': measurement.baseDuration,
      'react.start_time_ms': measurement.startTime,
      'react.commit_time_ms': measurement.commitTime,
    });
    span.setStatus({ code: SpanStatusCode.OK });
    span.end();
  });
}

